import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, FlaskConical, Search, Upload, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import AdminPanel from "@/components/admin/AdminPanel";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { request, uploadFile } from "@/lib/api";

const UNMAPPED = "__unmapped__";
const PAGE_SIZE = 25;

function money(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

export default function LabTestManager({ token, clientId, clientName, open = true, onClose, inline = false }) {
  const [tab, setTab] = useState("tests");
  const [catalogue, setCatalogue] = useState({ tests: [], total: 0 });
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [provider, setProvider] = useState("Healthians");
  // null -> file chosen (mapping) -> validated -> committed
  const [upload, setUpload] = useState(null);
  const [mapping, setMapping] = useState({});
  const [deactivateMissing, setDeactivateMissing] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const loadCatalogue = useCallback(async () => {
    if (!clientId) return;
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (search.trim()) params.set("search", search.trim());
      setCatalogue(await request(`/admin/clients/${clientId}/lab-tests?${params}`, {}, token));
    } catch (loadError) {
      setError(loadError.message);
    }
  }, [clientId, token, search, offset]);

  useEffect(() => {
    if (!open) return;
    // Start each visit clean: a half-finished mapping from last time is a
    // very easy way to apply the wrong one.
    setUpload(null);
    setMapping({});
    setError("");
    setNotice("");
    loadCatalogue();
  }, [open, loadCatalogue]);

  async function chooseFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const result = await uploadFile(
        `/admin/clients/${clientId}/lab-tests/upload?provider=${encodeURIComponent(provider)}`,
        file,
        token,
      );
      setUpload(result);
      setMapping(result.suggested_mapping || {});
    } catch (uploadError) {
      setError(uploadError.message);
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  }

  async function validate() {
    setBusy(true);
    setError("");
    try {
      const clean = Object.fromEntries(Object.entries(mapping).filter(([, column]) => column && column !== UNMAPPED));
      setUpload(
        await request(
          `/admin/lab-tests/uploads/${upload.upload_id}/validate`,
          { method: "POST", body: JSON.stringify({ mapping: clean }) },
          token,
        ),
      );
    } catch (validateError) {
      setError(validateError.message);
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    setBusy(true);
    setError("");
    try {
      const result = await request(
        `/admin/lab-tests/uploads/${upload.upload_id}/commit`,
        { method: "POST", body: JSON.stringify({ deactivate_missing: deactivateMissing }) },
        token,
      );
      setUpload(result);
      setNotice(
        `${result.created_count} added, ${result.updated_count} repriced` +
          (result.deactivated_count ? `, ${result.deactivated_count} retired` : "") +
          " — live for employees now.",
      );
      setTab("tests");
      await loadCatalogue();
    } catch (commitError) {
      setError(commitError.message);
    } finally {
      setBusy(false);
    }
  }

  async function discard() {
    if (upload && upload.status !== "committed") {
      try {
        await request(`/admin/lab-tests/uploads/${upload.upload_id}`, { method: "DELETE" }, token);
      } catch {
        /* the staged rows expire on their own; nothing here depends on it */
      }
    }
    setUpload(null);
    setMapping({});
  }

  async function saveTest(test, changes) {
    try {
      await request(`/admin/lab-tests/${test.id}`, { method: "PUT", body: JSON.stringify(changes) }, token);
      await loadCatalogue();
    } catch (saveError) {
      setError(saveError.message);
    }
  }

  const fields = upload?.fields || [];
  const columns = upload?.columns || [];
  const totalPages = Math.max(1, Math.ceil(catalogue.total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <AdminPanel inline={inline} open={open} onClose={onClose} title={`Lab tests — ${clientName || clientId || ""}`} testId="admin-lab-tests">

        {error && <p className="form-error" role="alert" data-testid="lab-admin-error">{error}</p>}
        {notice && <p className="profile-saved" role="status" data-testid="lab-admin-notice">{notice}</p>}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="tests" data-testid="lab-admin-tab-tests">Catalogue ({catalogue.total})</TabsTrigger>
            <TabsTrigger value="upload" data-testid="lab-admin-tab-upload">Upload a price list</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === "tests" && (
          <>
            <div className="center-search">
              <Search size={17} />
              <input
                type="search"
                value={search}
                onChange={(event) => { setSearch(event.target.value); setOffset(0); }}
                placeholder="Search this catalogue"
                aria-label="Search lab tests"
                data-testid="lab-admin-search"
              />
            </div>
            <div className="package-table-wrap">
              <Table data-testid="lab-admin-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Test</TableHead>
                    <TableHead>List price</TableHead>
                    <TableHead>Employee pays</TableHead>
                    <TableHead>Our cost</TableHead>
                    <TableHead>Margin</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {catalogue.tests.map((test) => (
                    <LabTestRow key={test.id} test={test} onSave={saveTest} />
                  ))}
                  {!catalogue.tests.length && (
                    <TableRow>
                      <TableCell colSpan={6} className="employee-muted">
                        {search ? "Nothing matches that search." : "No price list has been uploaded for this organisation yet."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            {totalPages > 1 && (
              <div className="lab-pagination" data-testid="lab-admin-pagination">
                <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setOffset(offset - PAGE_SIZE)}>Previous</Button>
                <span>Page {currentPage} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</Button>
              </div>
            )}
          </>
        )}

        {tab === "upload" && !upload && (
          <div className="package-edit" data-testid="lab-upload-start">
            <p className="employee-muted">
              Upload the partner's price list as .csv or .xlsx. You'll confirm which of their columns is which before
              anything goes live — nothing is priced for employees until you apply it.
            </p>
            <label htmlFor="lab-provider">Partner</label>
            <Input id="lab-provider" value={provider} onChange={(event) => setProvider(event.target.value)} data-testid="lab-provider-input" />
            <label htmlFor="lab-file" className="upload-dropzone">
              <Upload size={20} />
              <span>{busy ? "Reading the file…" : "Choose a price list"}</span>
              <input id="lab-file" type="file" accept=".csv,.xlsx,.xlsm" onChange={chooseFile} disabled={busy || !provider.trim()} data-testid="lab-file-input" />
            </label>
          </div>
        )}

        {upload && upload.status !== "committed" && (
          <div className="package-edit" data-testid="lab-upload-mapping">
            <div className="upload-summary">
              <strong>{upload.filename}</strong>
              <span>{upload.total_rows.toLocaleString("en-IN")} rows</span>
              <Button type="button" variant="ghost" size="sm" onClick={discard} data-testid="lab-upload-discard"><X size={14} />Discard</Button>
            </div>

            <p className="employee-muted">
              Match each of our fields to a column in the file. Both price columns are named separately on purpose — which
              one the employee pays is your call against the agreement, not something we should guess from a heading.
            </p>

            {fields.map((field) => (
              <div key={field.key} className="form-stack" data-testid={`lab-map-${field.key}`}>
                <label htmlFor={`lab-map-${field.key}`}>
                  {field.label}{field.required ? " *" : ""}
                </label>
                <select
                  id={`lab-map-${field.key}`}
                  value={mapping[field.key] || UNMAPPED}
                  onChange={(event) => setMapping((current) => ({ ...current, [field.key]: event.target.value }))}
                  data-testid={`lab-map-select-${field.key}`}
                >
                  <option value={UNMAPPED}>— not in this file —</option>
                  {columns.map((column) => <option value={column} key={column}>{column}</option>)}
                </select>
                <small className="employee-muted">{field.help}</small>
              </div>
            ))}

            {upload.status === "validated" && (
              <div className="package-price-compare" data-testid="lab-upload-result">
                <div><span className="profile-field__label">Ready to apply</span><strong>{upload.valid_rows.toLocaleString("en-IN")}</strong></div>
                <div><span className="profile-field__label">Rows with problems</span><strong>{upload.error_rows.toLocaleString("en-IN")}</strong></div>
              </div>
            )}

            {upload.status === "validated" && upload.errors?.length > 0 && (
              <div className="upload-errors" data-testid="lab-upload-errors">
                <p className="package-loss-warning"><AlertTriangle size={15} />These rows won't be applied:</p>
                <ul>
                  {upload.errors.slice(0, 10).map((row) => (
                    <li key={row.row_number}><strong>Row {row.row_number}</strong> — {row.errors.join(" ")}</li>
                  ))}
                </ul>
              </div>
            )}

            {upload.status === "validated" && (
              <label className="roster-policy-toggle" data-testid="lab-deactivate-toggle">
                <input type="checkbox" checked={deactivateMissing} onChange={(event) => setDeactivateMissing(event.target.checked)} />
                <span>
                  Retire tests that aren't in this file. Leave this on for a full price list; turn it off if you're
                  uploading only part of one, or everything else will stop being orderable.
                </span>
              </label>
            )}

            <div className="upload-actions">
              <Button type="button" variant="outline" onClick={validate} disabled={busy} data-testid="lab-validate-button">
                {busy ? "Checking…" : "Check the file"}
              </Button>
              <Button type="button" onClick={commit} disabled={busy || upload.status !== "validated" || !upload.valid_rows} data-testid="lab-commit-button">
                <CheckCircle2 size={15} />Apply {upload.valid_rows ? upload.valid_rows.toLocaleString("en-IN") : ""} tests
              </Button>
            </div>
          </div>
        )}

        {upload?.status === "committed" && (
          <div className="complimentary-confirmation" data-testid="lab-upload-done">
            <FlaskConical size={22} />
            <div>
              <p className="eyebrow">Applied</p>
              <p>{upload.created_count} added · {upload.updated_count} repriced · {upload.deactivated_count} retired</p>
              <Button variant="outline" size="sm" onClick={() => { setUpload(null); setMapping({}); }} data-testid="lab-upload-another">Upload another</Button>
            </div>
          </div>
        )}
    </AdminPanel>
  );
}

function LabTestRow({ test, onSave }) {
  const [price, setPrice] = useState(String(test.price));
  const [editing, setEditing] = useState(false);
  const margin = test.partner_cost != null ? test.price - test.partner_cost : null;

  return (
    <TableRow data-testid={`lab-admin-row-${test.id}`} className={test.is_active ? "" : "is-muted"}>
      <TableCell>
        <span className="package-name">{test.name}</span>
        <small className="employee-muted">
          {test.provider}{test.external_id ? ` · ${test.external_id}` : ""}
          {test.is_active ? "" : " · retired"}
        </small>
      </TableCell>
      <TableCell>{money(test.mrp)}</TableCell>
      <TableCell>
        {editing ? (
          <Input value={price} onChange={(event) => setPrice(event.target.value)} data-testid={`lab-price-input-${test.id}`} />
        ) : (
          <>
            {money(test.price)} <Badge variant="outline">{test.discount_percent}% off</Badge>
          </>
        )}
      </TableCell>
      <TableCell className="employee-muted">{test.partner_cost != null ? money(test.partner_cost) : "—"}</TableCell>
      <TableCell className={margin != null && margin < 0 ? "package-loss-warning" : ""}>
        {margin != null ? money(margin) : "—"}
      </TableCell>
      <TableCell>
        {editing ? (
          <Button size="sm" onClick={() => { onSave(test, { price: Number(price) }); setEditing(false); }} data-testid={`lab-save-${test.id}`}>Save</Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`lab-edit-${test.id}`}>Edit</Button>
        )}
      </TableCell>
    </TableRow>
  );
}
